import { useState, useEffect, useRef } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { useReference } from "@/hooks/useReference";
import { fetchFilters, createFilter, deleteFilter, getAccessToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  ArrowCounterClockwise, MagnifyingGlass, X, SlidersHorizontal, CaretDown, FloppyDisk, TrashSimple,
  Calendar as CalendarIcon,
} from "@phosphor-icons/react";
import { format, parseISO, isValid } from "date-fns";
import { toast } from "sonner";

const ALL = "__all";

const FieldSelect = ({ label, required, value, onChange, items, placeholder, testid }) => {
  const currentValue =
    value !== undefined && value !== null && value !== ""
      ? String(value)
      : ALL;

  return (
    <div className="space-y-1 min-w-0">
      <Label className="text-[11px] font-semibold text-primary/90">
        {label}{required && <span className="text-primary ml-0.5">*</span>}
      </Label>
      <Select
        value={currentValue}
        onValueChange={(v) => onChange(v === ALL ? "" : v)}
      >
        <SelectTrigger data-testid={testid} className="h-10 rounded-lg bg-white text-sm">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-h-[320px]">
          <SelectItem value={ALL}>{placeholder}</SelectItem>
          {items?.map((it) => (
            <SelectItem key={it.value} value={String(it.value)}>
              {it.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

const MultiFieldSelect = ({ label, required, value, onChange, items, placeholder, testid }) => {
  const [open, setOpen] = useState(false);
  const selectedIds = value ? String(value).split(",").filter(Boolean) : [];

  const toggle = (id) => {
    const strId = String(id);
    const next = selectedIds.includes(strId)
      ? selectedIds.filter((v) => v !== strId)
      : [...selectedIds, strId];
    onChange(next.join(","));
  };

  const selectedLabels = items
    ?.filter((it) => selectedIds.includes(String(it.value)))
    .map((it) => it.label) || [];

  const displayText =
    selectedLabels.length === 0
      ? placeholder
      : selectedLabels.length === 1
        ? selectedLabels[0]
        : `${selectedLabels[0]} +${selectedLabels.length - 1}`;

  return (
    <div className="space-y-1 min-w-0">
      <Label className="text-[11px] font-semibold text-primary/90">
        {label}{required && <span className="text-primary ml-0.5">*</span>}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testid}
            className="h-10 w-full rounded-lg bg-white text-sm border border-input px-3 flex items-center justify-between hover:border-primary/60 transition-colors"
          >
            <span className={`truncate text-left ${selectedIds.length ? "text-foreground" : "text-muted-foreground"}`}>
              {displayText}
            </span>
            <span className="flex items-center gap-1 shrink-0">
              {selectedIds.length > 0 && (
                <X
                  size={12}
                  weight="bold"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange("");
                  }}
                />
              )}
              <CaretDown size={12} weight="bold" className="text-muted-foreground" />
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[240px] p-1 max-h-[320px] overflow-y-auto" align="start">
          {items?.map((it) => {
            const checked = selectedIds.includes(String(it.value));
            return (
              <label
                key={it.value}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-secondary cursor-pointer text-sm"
              >
                <Checkbox checked={checked} onCheckedChange={() => toggle(it.value)} />
                <span className="truncate">{it.label}</span>
              </label>
            );
          })}
          {!items?.length && (
            <div className="px-2 py-1.5 text-xs text-muted-foreground">Boşdur</div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

const NumField = ({ label, values, onChange, keys, suffix, testids }) => (
  <div className="space-y-1 min-w-0">
    <Label className="text-[11px] font-semibold text-primary/90">
      {label}{suffix && <span className="text-muted-foreground font-normal ml-1">{suffix}</span>}
    </Label>
    <div className="flex gap-1">
      <Input
        data-testid={testids[0]}
        type="number" inputMode="numeric" placeholder="Min"
        value={values[keys[0]] || ""}
        onChange={(e) => onChange(keys[0], e.target.value)}
        className="h-10 rounded-lg bg-white text-sm flex-1 min-w-0"
      />
      <Input
        data-testid={testids[1]}
        type="number" inputMode="numeric" placeholder="Max"
        value={values[keys[1]] || ""}
        onChange={(e) => onChange(keys[1], e.target.value)}
        className="h-10 rounded-lg bg-white text-sm flex-1 min-w-0"
      />
    </div>
  </div>
);

const TextField = ({ label, value, onChange, placeholder, testid }) => (
  <div className="space-y-1 min-w-0">
    <Label className="text-[11px] font-semibold text-primary/90">{label}</Label>
    <Input
      data-testid={testid}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-10 rounded-lg bg-white text-sm"
    />
  </div>
);

/* ---------- YENİ: DateField + DateRange (əl ilə yazmaq mümkün deyil, yalnız təqvimdən seçim) ---------- */

const DateField = ({ value, onChange, placeholder, testid }) => {
  const [open, setOpen] = useState(false);

  const parsedDate = value && isValid(parseISO(value)) ? parseISO(value) : undefined;

  const handleSelect = (date) => {
    if (!date) {
      onChange("");
      setOpen(false);
      return;
    }
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    onChange(`${y}-${m}-${d}`);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className="h-10 w-full rounded-lg bg-white text-sm border border-input px-3 flex items-center justify-between hover:border-primary/60 transition-colors flex-1 min-w-0"
        >
          <span className={`truncate text-left ${parsedDate ? "text-foreground" : "text-muted-foreground"}`}>
            {parsedDate ? format(parsedDate, "dd/MM/yyyy") : placeholder}
          </span>
          <CalendarIcon size={14} className="text-muted-foreground shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={parsedDate}
          onSelect={handleSelect}
          captionLayout="dropdown"
          fromYear={2015}
          toYear={new Date().getFullYear() + 1}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

const DateRange = ({ label, values, onChange, keys, testids }) => (
  <div className="space-y-1 min-w-0">
    <Label className="text-[11px] font-semibold text-primary/90">{label}</Label>
    <div className="flex gap-1">
      <DateField
        testid={testids[0]}
        value={values[keys[0]]}
        onChange={(v) => onChange(keys[0], v)}
        placeholder="Başlanğıc"
      />
      <DateField
        testid={testids[1]}
        value={values[keys[1]]}
        onChange={(v) => onChange(keys[1], v)}
        placeholder="Bitiş"
      />
    </div>
  </div>
);

/* ---------- YENİ HİSSƏNİN SONU ---------- */

const BoolCheck = ({ label, value, onChange, testid }) => (
  <label className="flex items-center gap-2 h-10 px-3 rounded-lg bg-white border border-input cursor-pointer text-sm hover:border-primary/60 transition-colors">
    <Checkbox
      data-testid={testid}
      checked={!!value}
      onCheckedChange={(v) => onChange(v ? "1" : "")}
    />
    <span className="font-medium">{label}</span>
  </label>
);

const REF_KEYS = [
  "ownerTypes", "operationTypes", "propertyTypes", "regions", "metros",
  "buildingTypes", "roomCounts", "targets", "repairRates", "documents",
];

const isRefReady = (ref) => {
  if (!ref) return false;
  return REF_KEYS.every((k) => Array.isArray(ref[k]) && ref[k].length > 0);
};

export const HorizontalFilterBar = ({ filters, setFilters, onApply, onReset }) => {
  const { t } = useLang();
  const { ref } = useReference();
  const [advOpen, setAdvOpen] = useState(false);
  const upd = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));
  const activeCount = Object.values(filters).filter(Boolean).length;

  const isLoggedIn = !!getAccessToken();

  const [savedFilters, setSavedFilters] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [selectedSavedId, setSelectedSavedId] = useState("");
  const [savedPopoverOpen, setSavedPopoverOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newFilterName, setNewFilterName] = useState("");
  const [savingFilter, setSavingFilter] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingFilter, setDeletingFilter] = useState(false);
  const [filterVersion, setFilterVersion] = useState(0);
  const selectedSavedName = savedFilters.find((f) => f.value === selectedSavedId)?.label || "";

  const pendingSavedData = useRef(null);
  const refReady = isRefReady(ref);

  const loadSavedFilters = async () => {
    if (!isLoggedIn) return;
    setLoadingSaved(true);
    try {
      const list = await fetchFilters();
      setSavedFilters(Array.isArray(list) ? list : []);
    } catch (e) {
      setSavedFilters([]);
    } finally {
      setLoadingSaved(false);
    }
  };

  useEffect(() => {
    loadSavedFilters();
  }, [isLoggedIn]);

  const applyParsedFilter = (parsed) => {
    setFilters((prev) => {
      const next = { ...prev };
      Object.keys(parsed).forEach((key) => {
        const val = parsed[key];
        if (val !== null && val !== undefined && val !== "") {
          next[key] = val;
        }
      });
      return next;
    });
    setFilterVersion((v) => v + 1);
  };

  useEffect(() => {
    if (refReady && pendingSavedData.current) {
      applyParsedFilter(pendingSavedData.current);
      pendingSavedData.current = null;
    }
  }, [refReady]);

  const openSaveDialog = () => {
    if (activeCount === 0) {
      toast("Saxlamaq üçün ən azı bir filter seçin");
      return;
    }
    setNewFilterName(`Filter ${savedFilters.length + 1}`);
    setSaveDialogOpen(true);
  };

  const confirmSaveFilter = async () => {
    const name = newFilterName.trim() || `Filter ${savedFilters.length + 1}`;
    setSavingFilter(true);
    try {
      await createFilter({ name, data: JSON.stringify(filters) });
      toast.success("Filter yadda saxlanıldı");
      setSaveDialogOpen(false);
      await loadSavedFilters();
    } catch (e) {
      toast.error(e?.message || "Filter saxlanılmadı");
    } finally {
      setSavingFilter(false);
    }
  };

  const onSelectSaved = (id) => {
    setSelectedSavedId(id);
    const found = savedFilters.find((f) => f.value === id);
    if (found) {
      try {
        const parsed = JSON.parse(found.data);
        if (refReady) {
          applyParsedFilter(parsed);
        } else {
          pendingSavedData.current = parsed;
          toast("Məlumatlar yüklənir, filter tam tətbiq olunacaq...");
        }
      } catch {
        toast.error("Filter oxuna bilmədi");
      }
    }
    setSavedPopoverOpen(false);
  };

  const handleReset = () => {
    setSelectedSavedId("");
    pendingSavedData.current = null;
    setFilterVersion((v) => v + 1);
    onReset();
  };

  const clearSelectedSaved = (e) => {
    e.stopPropagation();
    setSelectedSavedId("");
  };

  const requestDeleteSaved = (f) => {
    setDeleteTarget(f);
  };

  const confirmDeleteSaved = async () => {
    if (!deleteTarget) return;
    setDeletingFilter(true);
    try {
      await deleteFilter(deleteTarget.value);
      setSavedFilters((prev) => prev.filter((f) => f.value !== deleteTarget.value));
      if (deleteTarget.value === selectedSavedId) setSelectedSavedId("");
      toast.success("Filter silindi");
    } catch (e) {
      toast.error(e?.message || "Filter silinmədi");
    } finally {
      setDeletingFilter(false);
      setDeleteTarget(null);
    }
  };

  return (
    <div
      data-testid="horizontal-filter-bar"
      className="rounded-2xl border border-border/60 bg-card p-3 md:p-4 shadow-sm"
    >
      <div key={`row1-${filterVersion}`} className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
        <FieldSelect
          testid="filter-owner-type" label="Satıcı tipi" required
          value={filters.idOwnerType}
          onChange={(v) => upd("idOwnerType", v)}
          items={ref?.ownerTypes}
          placeholder="Satıcı tipi"
        />
        <FieldSelect
          testid="filter-operation" label="Elanın növü" required
          value={filters.idOperationType}
          onChange={(v) => upd("idOperationType", v)}
          items={ref?.operationTypes}
          placeholder="Elan növü"
        />
        <MultiFieldSelect
          testid="filter-property-type" label="Əmlakın növü" required
          value={filters.idPropertyType}
          onChange={(v) => upd("idPropertyType", v)}
          items={ref?.propertyTypes}
          placeholder="Əmlak növü"
        />
        <MultiFieldSelect
          testid="filter-region" label="Rayon"
          value={filters.regionIds}
          onChange={(v) => upd("regionIds", v)}
          items={ref?.regions}
          placeholder="Rayonu seç"
        />
        <MultiFieldSelect
          testid="filter-metro" label="Metro"
          value={filters.metroIds}
          onChange={(v) => upd("metroIds", v)}
          items={ref?.metros}
          placeholder="Metronu seç"
        />
      </div>

      <div key={`row2-${filterVersion}`} className={`grid gap-3 grid-cols-2 md:grid-cols-3 ${isLoggedIn ? "lg:grid-cols-5" : "lg:grid-cols-4"} mt-3`}>
        <NumField
          label="Sahə" suffix="kv²"
          values={filters} keys={["landAreaMin", "landAreaMax"]}
          onChange={upd} testids={["filter-land-min", "filter-land-max"]}
        />
        <FieldSelect
          testid="filter-building-type" label="Binanın növü"
          value={filters.buildingTypeIds}
          onChange={(v) => upd("buildingTypeIds", v)}
          items={ref?.buildingTypes}
          placeholder="Binanın növünü seç"
        />
        <NumField
          label="Qiymət" suffix="AZN"
          values={filters} keys={["priceMin", "priceMax"]}
          onChange={upd} testids={["filter-price-min", "filter-price-max"]}
        />
        <MultiFieldSelect
          testid="filter-rooms" label="Otaq"
          value={filters.roomIds}
          onChange={(v) => upd("roomIds", v)}
          items={ref?.roomCounts}
          placeholder="Otaq seç"
        />

        {isLoggedIn && (
          <div className="space-y-1 min-w-0">
            <Label className="text-[11px] font-semibold text-primary/90">Sözə görə</Label>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                data-testid="filter-q"
                value={filters.keyword || ""}
                onChange={(e) => upd("keyword", e.target.value)}
                placeholder="Sözə görə axtarın"
                className="pl-8 pr-8 h-10 rounded-lg bg-white text-sm"
              />
              {filters.keyword && (
                <button
                  type="button"
                  onClick={() => upd("keyword", "")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full hover:bg-secondary flex items-center justify-center"
                  aria-label="clear"
                >
                  <X size={12} weight="bold" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <Collapsible open={advOpen} onOpenChange={setAdvOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            data-testid="filter-advanced-toggle"
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
          >
            <SlidersHorizontal size={13} weight="bold" />
            Ətraflı
            <CaretDown size={12} weight="bold" className={`transition-transform ${advOpen ? "rotate-180" : ""}`} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div key={`row3-${filterVersion}`} className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-6 mt-3">
            <MultiFieldSelect
              testid="filter-target" label="Nişangah"
              value={filters.targetIds}
              onChange={(v) => upd("targetIds", v)}
              items={ref?.targets}
              placeholder="Nişangah seç"
            />
            <DateRange
              label="Elanın tarixi"
              values={filters} keys={["startDate", "endDate"]}
              onChange={upd}
              testids={["filter-date-start", "filter-date-end"]}
            />

            <NumField
              label="Torpaq sot" suffix=""
              values={filters} keys={["areaMin", "areaMax"]}
              onChange={upd} testids={["filter-area-min", "filter-area-max"]}
            />
            <div className="lg:col-span-1 flex items-end">
              <BoolCheck
                testid="filter-only-extract" label="Yalnız çıxarışlı"
                value={filters.isOnlyExtract}
                onChange={(v) => upd("isOnlyExtract", v)}
              />
            </div>
            <NumField
              label="Mərtəbə" values={filters} keys={["floorMin", "floorMax"]}
              onChange={upd} testids={["filter-floor-min", "filter-floor-max"]}
            />
          </div>

          <div key={`row4-${filterVersion}`} className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-6 mt-3">
            <TextField
              testid="filter-phone" label="Telefon"
              value={filters.phoneNumber}
              onChange={(v) => upd("phoneNumber", v)}
              placeholder="Telefon nömrəsi"
            />
            <div className="flex items-end">
              <BoolCheck
                testid="filter-only-credit" label="Yalnız kreditli"
                value={filters.isOnlyLoan}
                onChange={(v) => upd("isOnlyLoan", v)}
              />
            </div>
            <NumField
              label="Binanın mərtəbə sayı"
              values={filters} keys={["buildingfloorMin", "buildingfloorMax"]}
              onChange={upd} testids={["filter-floor-of-min", "filter-floor-of-max"]}
            />
            <FieldSelect
              testid="filter-repair" label="Təmir növü"
              value={filters.repairRateIds}
              onChange={(v) => upd("repairRateIds", v)}
              items={ref?.repairRates}
              placeholder="Təmiri seç"
            />
            <div className="flex items-end">
              <BoolCheck
                testid="filter-not-last-floor" label="Axırıncı mərtəbə olmasın"
                value={filters.isNotLastFloor}
                onChange={(v) => upd("isNotLastFloor", v)}
              />
            </div>
            <FieldSelect
              testid="filter-document" label="Sənəd"
              value={filters.idDocument}
              onChange={(v) => upd("idDocument", v)}
              items={ref?.documents}
              placeholder="Sənəd"
            />
          </div>
        </CollapsibleContent>
      </Collapsible>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          data-testid="filter-apply-btn" onClick={onApply}
          className="h-10 px-5 rounded-lg font-semibold gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          <MagnifyingGlass size={14} weight="bold" /> {t("common.search")}
        </Button>
        <Button
          variant="outline" data-testid="filter-reset-btn" onClick={handleReset}
          className="h-10 rounded-lg font-medium gap-1.5"
        >
          <ArrowCounterClockwise size={14} weight="bold" /> {t("common.reset")}
        </Button>

        {isLoggedIn && (
          <>
            <Button
              variant="outline" data-testid="filter-save-btn" onClick={openSaveDialog}
              className="h-10 rounded-lg font-medium gap-1.5"
            >
              <FloppyDisk size={14} weight="bold" /> Filteri saxla
            </Button>
            <Popover open={savedPopoverOpen} onOpenChange={setSavedPopoverOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  disabled={loadingSaved}
                  data-testid="filter-saved-select"
                  className="h-10 w-[180px] rounded-lg bg-background text-sm border border-input px-3 flex items-center justify-between hover:border-primary/60 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className={`truncate text-left ${selectedSavedName ? "text-foreground" : "text-muted-foreground"}`}>
                    {selectedSavedName || "Saxlanılan filterlər"}
                  </span>
                  <span className="flex items-center gap-1 shrink-0">
                    {selectedSavedId && (
                      <X
                        size={12}
                        weight="bold"
                        className="text-muted-foreground hover:text-foreground"
                        onClick={clearSelectedSaved}
                      />
                    )}
                    <CaretDown size={12} weight="bold" className="text-muted-foreground" />
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[240px] p-1 max-h-[320px] overflow-y-auto" align="start">
                {loadingSaved ? (
                  <div className="px-2 py-1.5 text-xs text-muted-foreground">Yüklənir...</div>
                ) : savedFilters.length === 0 ? (
                  <div className="px-2 py-1.5 text-xs text-muted-foreground">Saxlanılan filter yoxdur</div>
                ) : (
                  <>
                    {selectedSavedId && (
                      <button
                        type="button"
                        data-testid="saved-filter-clear"
                        onClick={() => { setSelectedSavedId(""); setSavedPopoverOpen(false); }}
                        className="w-full text-left px-2 py-1.5 text-sm rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
                      >
                        Seçimi ləğv et
                      </button>
                    )}
                    {savedFilters.map((f) => (
                      <div
                        key={f.value}
                        className={`flex items-center gap-0.5 rounded-md ${f.value === selectedSavedId ? "bg-secondary" : "hover:bg-secondary"}`}
                      >
                        <button
                          type="button"
                          data-testid={`saved-filter-${f.value}`}
                          onClick={() => onSelectSaved(f.value)}
                          className="flex-1 min-w-0 text-left px-2 py-1.5 text-sm truncate"
                        >
                          {f.label}
                        </button>
                        <button
                          type="button"
                          data-testid={`saved-filter-delete-${f.value}`}
                          onClick={(e) => { e.stopPropagation(); requestDeleteSaved(f); }}
                          aria-label={`"${f.label}" filterini sil`}
                          title="Sil"
                          className="w-7 h-7 mr-0.5 shrink-0 rounded-md flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                        >
                          <TrashSimple size={13} weight="bold" />
                        </button>
                      </div>
                    ))}
                  </>
                )}
              </PopoverContent>
            </Popover>
          </>
        )}

        {activeCount > 0 && (
          <span className="ml-auto text-xs text-muted-foreground">
            <span className="font-semibold text-primary">{activeCount}</span> aktiv filter · avtomatik yenilənir
          </span>
        )}
      </div>

      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="save-filter-dialog">
          <DialogHeader>
            <DialogTitle>Filteri saxla</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold text-primary/90">Filterin adı</Label>
            <Input
              data-testid="save-filter-name-input"
              value={newFilterName}
              onChange={(e) => setNewFilterName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !savingFilter && confirmSaveFilter()}
              placeholder="Məs: 2 otaqlı Nəsimi kirayə"
              className="h-10 rounded-lg bg-white text-sm"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              data-testid="save-filter-confirm-btn"
              onClick={confirmSaveFilter}
              disabled={savingFilter}
              className="h-10 rounded-lg font-semibold bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {savingFilter ? "Saxlanılır..." : "Saxla"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-filter-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Filteri sil</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.label}" adlı filteri silmək istədiyinizə əminsiniz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingFilter}>Ləğv et</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-filter-confirm-btn"
              onClick={confirmDeleteSaved}
              disabled={deletingFilter}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deletingFilter ? "Silinir..." : "Sil"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};